// ─────────────────────────────────────────────────────────────────────────────
// dataInsights — LE cerveau d'analyse croisée de la base clients.
//
// Source UNIQUE, réutilisée par : la page Contacts, le Génie coach (advice.js),
// le Dashboard, l'Onboarding. Fini les analyses éparpillées qui ne se croisent
// jamais. Chaque brique est atomique et exportée ; `computeInsights()` assemble
// une liste PRIORISÉE (légal > injoignable > rétention > doublon > incomplet > croissance).
// ─────────────────────────────────────────────────────────────────────────────
import { isVip } from './segments';

export function normalize(s) {
  return (s || '').toString().toLowerCase().trim()
    .normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// Canonicalise un numéro suisse : +41 79… / 0041… / 079… → 79… (national).
// C'est LE fix du bug « 473 doublons » : avant, +41791756865 ≠ 41791756865.
export function normalizePhone(p) {
  let d = (p || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('0041')) d = d.slice(4);
  else if (d.startsWith('41') && d.length >= 10) d = d.slice(2);
  if (d.startsWith('0') && d.length >= 9) d = d.slice(1);
  return d.length >= 8 ? d : '';
}

// Mobile suisse = préfixes 074, 075, 076, 077, 078, 079 (local, +41 ou 0041).
// Verrou : ne PAS oublier 074/075 (Sunrise/MVNO), sinon ces contacts sont
// classés « non-SMSables » à tort dans le ciblage.
export function isSwissMobile(phone) {
  const clean = String(phone || '').replace(/[\s\-.()]/g, '');
  return /^\+417[4-9]\d{7}$/.test(clean)
      || /^00417[4-9]\d{7}$/.test(clean)
      || /^07[4-9]\d{7}$/.test(clean);
}

// Une date de naissance « réelle » (pas la sentinelle jour/mois-seul, année ≤ 1904).
const SENTINEL_YEAR = 1904;
function realBirth(bd) {
  if (!bd) return null;
  const y = parseInt(String(bd).split('-')[0], 10);
  return (y && y > SENTINEL_YEAR) ? bd : null;
}

// Doublons — signal FORT requis : même e-mail, OU même téléphone normalisé, OU
// même nom complet + même date de naissance RÉELLE. On ne déclenche plus sur
// « même nom + date sentinelle » (cause du sur-comptage massif). O(n) par regroupement.
export function findDuplicates(contacts = []) {
  const pairs = [];
  const seen = new Set();
  const addPair = (a, b, reasons) => {
    const key = [a.id, b.id].sort().join('-');
    if (seen.has(key)) return;
    seen.add(key);
    pairs.push({ a, b, reasons, key });
  };
  const chain = (map, reason) => map.forEach(g => {
    for (let x = 0; x + 1 < g.length; x++) addPair(g[x], g[x + 1], [reason]);
  });

  const byEmail = new Map();
  contacts.forEach(c => { if (c.email) { const k = normalize(c.email); if (!byEmail.has(k)) byEmail.set(k, []); byEmail.get(k).push(c); } });
  chain(byEmail, 'Même e-mail');

  const byPhone = new Map();
  contacts.forEach(c => { const k = normalizePhone(c.phone); if (k) { if (!byPhone.has(k)) byPhone.set(k, []); byPhone.get(k).push(c); } });
  chain(byPhone, 'Même téléphone');

  const byName = new Map();
  contacts.forEach(c => {
    const fn = normalize(c.first_name), ln = normalize(c.last_name);
    if (fn.length > 1 && ln.length > 1) { const k = `${fn}|${ln}`; if (!byName.has(k)) byName.set(k, []); byName.get(k).push(c); }
  });
  byName.forEach(group => {
    for (let x = 0; x < group.length; x++) for (let y = x + 1; y < group.length; y++) {
      const a = group[x], b = group[y];
      const ba = realBirth(a.birth_date), bb = realBirth(b.birth_date);
      if (ba && bb && ba === bb) addPair(a, b, ['Même nom', 'Même date de naissance']);
    }
  });
  return pairs;
}

export function duplicateContactCount(contacts = []) {
  const ids = new Set();
  findDuplicates(contacts).forEach(p => { ids.add(p.a.id); ids.add(p.b.id); });
  return ids.size;
}

// ── Croisements de qualité / joignabilité ────────────────────────────────────
export const unreachable      = (c = []) => c.filter(x => !x.email && !x.phone);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const invalidEmails    = (c = []) => c.filter(x => x.email && !EMAIL_RE.test(String(x.email).trim()));
export const invalidPhones    = (c = []) => c.filter(x => x.phone && normalizePhone(x.phone) === '');
export const noConsentList    = (c = []) => c.filter(x => x.gdpr_consent === false || x.sms_consent === false);

function isDormant(x, now12) {
  return !x.last_interaction || new Date(x.last_interaction) < now12;
}

// VIP dormant = croisement VALEUR × INACTIVITÉ → l'urgence de rétention la plus rentable.
export function vipDormant(c = []) {
  const now12 = new Date(); now12.setFullYear(now12.getFullYear() - 1);
  return c.filter(x => isVip(x) && isDormant(x, now12));
}

// Anniversaire ce mois mais injoignable (ni e-mail ni téléphone).
export function birthdaySoonUnreachable(c = []) {
  const m = new Date().getMonth() + 1;
  return c.filter(x => x.birth_date && parseInt(String(x.birth_date).split('-')[1], 10) === m && !x.email && !x.phone);
}

// Pression quota (contacts/emails/sms) selon le plan.
export function quotaPressure(tenant = {}) {
  const out = [];
  const pct = (used, limit) => (limit > 0 ? used / limit : 0);
  const contacts = pct(tenant.contacts_used, tenant.contacts_limit);
  const emails   = pct(tenant.emails_used,   tenant.emails_limit);
  if (contacts >= 0.85) out.push({ kind: 'contacts', pct: Math.round(contacts * 100) });
  if (emails   >= 0.85) out.push({ kind: 'emails',   pct: Math.round(emails * 100) });
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// computeInsights — liste PRIORISÉE d'insights actionnables, tous croisements confondus.
// Chaque insight : { id, severity (1=urgent…7), icon, title, text, count, link }.
// ─────────────────────────────────────────────────────────────────────────────
export function computeInsights(state = {}) {
  const { contacts = [], tenant = {} } = state;
  const out = [];
  const push = (o) => out.push(o);

  const nContacts = contacts.length;
  const noConsent = noConsentList(contacts);
  const mute      = unreachable(contacts);
  const badEmail  = invalidEmails(contacts);
  const vipZzz    = vipDormant(contacts);
  const dupCount  = duplicateContactCount(contacts);
  const bdayMute  = birthdaySoonUnreachable(contacts);
  const quota     = quotaPressure(tenant);

  // 1 — LÉGAL (bloquant)
  if (nContacts > 0 && noConsent.length / nContacts >= 0.5) {
    push({ id: 'legal-consent', severity: 1, icon: '🛡️', count: noConsent.length,
      title: `${noConsent.length} contacts sans consentement`,
      text: "La loi suisse (nLPD) exige de prouver leur accord. Régularise avant d'envoyer une campagne.", link: '/contacts' });
  }
  // 2 — INJOIGNABLE
  if (mute.length > 0) {
    push({ id: 'unreachable', severity: 2, icon: '🕳️', count: mute.length,
      title: `${mute.length} contacts injoignables`,
      text: 'Ni e-mail ni téléphone : impossible de les toucher. Complète une coordonnée.', link: '/contacts' });
  }
  if (badEmail.length > 0) {
    push({ id: 'invalid-email', severity: 2, icon: '📧', count: badEmail.length,
      title: `${badEmail.length} e-mails au format invalide`,
      text: 'Ces adresses feront échouer tes envois. Corrige-les.', link: '/contacts' });
  }
  // 3 — RÉTENTION URGENTE
  if (vipZzz.length > 0) {
    push({ id: 'vip-dormant', severity: 3, icon: '⚠️', count: vipZzz.length,
      title: `${vipZzz.length} VIP en train de partir`,
      text: 'Tes meilleurs clients n\'ont pas donné signe depuis 12 mois. Relance-les en priorité — c\'est le ROI n°1.', link: '/campaigns' });
  }
  // 4 — DOUBLON
  if (dupCount > 0) {
    push({ id: 'duplicates', severity: 4, icon: '👥', count: dupCount,
      title: `${dupCount} doublons dans ta base`,
      text: 'Fusionne-les pour éviter les doubles envois (et les désabonnements qui vont avec).', link: '/contacts' });
  }
  // 5 — ANNIVERSAIRE INJOIGNABLE
  if (bdayMute.length > 0) {
    push({ id: 'bday-mute', severity: 5, icon: '🎂', count: bdayMute.length,
      title: `${bdayMute.length} anniversaires ce mois… injoignables`,
      text: 'Ajoute-leur un e-mail ou un téléphone avant le jour J pour ne pas les rater.', link: '/contacts' });
  }
  // 6 — QUOTA
  quota.forEach(q => push({ id: `quota-${q.kind}`, severity: 6, icon: '📊', count: q.pct,
    title: `${q.kind === 'contacts' ? 'Base' : 'E-mails'} à ${q.pct}% de ta limite`,
    text: 'Tu approches la limite de ton plan. Anticipe une montée en gamme.', link: '/settings' }));

  return out.sort((a, b) => a.severity - b.severity);
}
